import { AriaRole, ReactNode, useCallback, useRef } from "react";

import { AnalyticsCommonProps } from "hooks/useAnalytics";

import { BoxProps } from "../../Box";
import { TypographyProps } from "../../Typography";
import { CardHeaderProps } from "../components/Header";
import CardCollapsibleContent from "./components/Content";
import CardCollapsibleHeader from "./components/Header";
import { CardCollapsibleAriaLevel } from "./components/types";
import CardCollapsibleWrapper from "./components/Wrapper";

type CardCollapsibleRole = Extract<
  AriaRole,
  "region" | "article" | "group" | "complementary" | "treeitem"
>;

type CardCollapsibleProps = {
  children: ReactNode;
  fullbleed?: boolean;
  variant?: "secondary" | "primary";
  title: ReactNode;
  titlePrefix?: ReactNode;
  titleTag?: TypographyProps["tag"];
  titleVariant?: CardHeaderProps["titleVariant"];
  className?: string;
  headerSuffix?: ReactNode;
  titleSuffix?: ReactNode;
  contentGap?: BoxProps["gap"];
  isCollapsed: boolean;
  onToggle: () => void;
  role?: CardCollapsibleRole;
  ariaLevel: CardCollapsibleAriaLevel;
  ariaLabel: string;
  headerAlign?: BoxProps["align"];
  scrollIntoView?: boolean;
} & Pick<BoxProps, "fullHeight" | "fullWidth" | "margin" | "padding"> &
  AnalyticsCommonProps;

const CardCollapsible = ({
  headerAlign = "center",
  children,
  fullbleed,
  variant,
  title,
  titlePrefix,
  titleVariant,
  className,
  headerSuffix,
  titleSuffix,
  titleTag,
  ariaLevel,
  ariaLabel,
  isCollapsed,
  onToggle,
  role,
  contentGap,
  analyticsPage,
  analyticsTitle,
  analyticsProps,
  scrollIntoView,
  ...restBoxProps
}: CardCollapsibleProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const handleToggle = useCallback(() => {
    if (scrollIntoView && isCollapsed) {
      const scroll = () => {
        containerRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });

        containerRef.current?.removeEventListener("transitionend", scroll);
      };

      containerRef.current?.addEventListener("transitionend", scroll);
    }

    onToggle();
  }, [scrollIntoView, isCollapsed, onToggle]);

  return (
    <CardCollapsibleWrapper
      ref={containerRef}
      variant={variant}
      className={className}
      isCollapsed={isCollapsed}
      onToggle={handleToggle}
      role={role}
      {...restBoxProps}
    >
      {title && (
        <CardCollapsibleHeader
          align={headerAlign}
          prefix={titlePrefix}
          titleTag={titleTag}
          titleVariant={titleVariant}
          titleSuffix={titleSuffix}
          suffix={headerSuffix}
          ariaLevel={ariaLevel}
          ariaLabel={ariaLabel}
          analyticsPage={analyticsPage}
          analyticsTitle={analyticsTitle}
          analyticsProps={analyticsProps}
        >
          {title}
        </CardCollapsibleHeader>
      )}
      <CardCollapsibleContent hasHeader={!!title} fullbleed={fullbleed} gap={contentGap}>
        {children}
      </CardCollapsibleContent>
    </CardCollapsibleWrapper>
  );
};

CardCollapsible.displayName = "DS.Card.Collapsible";

export default CardCollapsible;
